import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { fetchMe } from "@/features/auth/api/authApi";
import { useAuthStore } from "@/features/auth/model/authStore";

/**
 * Landing page for the Google OAuth callback redirect.
 *
 * Reads ``token`` and ``new`` from the query string, hydrates the auth store,
 * fetches the current user, and bounces to the appropriate landing route:
 *   - new=1 + role=WORKER  -> /worker/onboarding
 *   - role=WORKER          -> /worker
 *   - role=VENUE           -> /venue
 */
export function AuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const setSession = useAuthStore((s) => s.setSession);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = searchParams.get("token");
    const isNew = searchParams.get("new") === "1";
    const oauthError = searchParams.get("error");

    if (oauthError) {
      setError(oauthError);
      return;
    }

    if (!token) {
      setError("missing_token");
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        // Persist the token first so the axios interceptor picks it up for /me.
        localStorage.setItem("oneshift_token", token);
        const user = await fetchMe();
        if (cancelled) return;
        setSession(token, user);

        if (isNew && user.role === "WORKER") {
          navigate("/worker/onboarding", { replace: true });
        } else {
          navigate(user.role === "VENUE" ? "/venue" : "/worker", { replace: true });
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "auth_failed");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <div className="w-full max-w-sm space-y-4 text-center">
        {error ? (
          <>
            <p className="text-base font-medium text-copy">Sign-in failed</p>
            <p className="text-sm text-copy-muted">
              We couldn't complete your Google sign-in ({error}). Please try again.
            </p>
            <button
              type="button"
              onClick={() => navigate("/login", { replace: true })}
              className="text-sm font-medium text-primary hover:underline"
            >
              Back to sign in
            </button>
          </>
        ) : (
          <>
            <div
              className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-border border-t-primary"
              aria-hidden
            />
            <p className="text-sm text-copy-muted">Signing you in…</p>
          </>
        )}
      </div>
    </div>
  );
}
